import { displayTitle, type ResearchMode, type SourceKind } from '@buakei/types';
import { Button, DataOriginBadge, EmptyState, ErrorState, LoadingState, cn } from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { ActsAssistant } from '@/assistant/ActsAssistant';
import { useMemoWorkspace } from './MemoPages';
import { AssistantPanel } from '@/assistant/AssistantPanel';
import { CitationLink } from '@/inspector/CitationLink';
import {
  ActList,
  LibraryClosedNotice,
  ProvisionalNotice,
  useCatalogue,
  useReaderTokens,
} from '@/pages/library/LibraryPages';
import { demoResearchAssistant, demoTutorAssistant } from '@/services/assistant';
import { useAuth } from '@/auth/session';
import { demoLegalSourceService } from '@/services/legal-sources';
import { Page } from '@/shell/Page';

const MODES: { value: ResearchMode; label: string; available: boolean; note: string }[] = [
  { value: 'quick', label: 'Quick', available: true, note: 'Short sourced answer' },
  {
    value: 'comprehensive',
    label: 'Comprehensive',
    available: false,
    note: 'Not yet available',
  },
  { value: 'custom', label: 'Custom plan', available: false, note: 'Not yet available' },
];

/** ResearchModeSelector — unavailable modes are disabled with a stated reason. */
function ResearchModeSelector() {
  const [mode, setMode] = useState<ResearchMode>('quick');
  return (
    <fieldset className="flex flex-wrap gap-2">
      <legend className="mb-1 text-sm font-medium text-navy-900">Research mode</legend>
      {MODES.map((m) => (
        <label
          key={m.value}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm',
            mode === m.value
              ? 'border-navy-900 bg-white text-navy-900 ring-1 ring-navy-900'
              : 'border-line-200 text-ink-900',
            !m.available && 'cursor-not-allowed opacity-70',
          )}
        >
          <input
            type="radio"
            name="research-mode"
            value={m.value}
            checked={mode === m.value}
            disabled={!m.available}
            onChange={() => setMode(m.value)}
            className="accent-navy-900"
          />
          <span>{m.label}</span>
          <span className="text-xs text-muted-600">— {m.note}</span>
        </label>
      ))}
    </fieldset>
  );
}

const KINDS: { value: SourceKind; label: string }[] = [
  { value: 'statute', label: 'Statutes' },
  { value: 'judgment', label: 'Judgments' },
  { value: 'rule', label: 'Rules' },
];

/** Keyword search over the demo passages, with collection filters (M1-04). */
function SourceSearch() {
  const inputId = useId();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [kinds, setKinds] = useState<SourceKind[]>(KINDS.map((k) => k.value));

  const results = useQuery({
    queryKey: ['passage-search', query, kinds],
    queryFn: () => demoLegalSourceService.searchPassages(query, kinds),
    enabled: query.length > 0,
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setQuery(draft.trim());
  };

  return (
    <section aria-labelledby="source-search-heading" className="flex flex-col gap-4">
      <h2 id="source-search-heading" className="font-serif text-xl font-semibold text-navy-900">
        Search sources
      </h2>
      <form onSubmit={onSubmit} className="flex flex-col gap-3" role="search">
        <div className="flex gap-2">
          <label htmlFor={inputId} className="sr-only">
            Search terms
          </label>
          <input
            id={inputId}
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. acceptance, consideration, capacity"
            className="h-10 min-w-0 flex-1 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600"
          />
          <Button type="submit">
            <Search aria-hidden /> Search
          </Button>
        </div>
        <fieldset className="flex flex-wrap items-center gap-4 text-sm">
          <legend className="sr-only">Collections</legend>
          {KINDS.map((k) => (
            <label key={k.value} className="flex items-center gap-2 text-ink-900">
              <input
                type="checkbox"
                className="size-4 accent-navy-900"
                checked={kinds.includes(k.value)}
                onChange={(e) =>
                  setKinds((current) =>
                    e.target.checked ? [...current, k.value] : current.filter((v) => v !== k.value),
                  )
                }
              />
              {k.label}
            </label>
          ))}
          <span className="text-muted-600">
            Court and date filters arrive with the real corpus.
          </span>
        </fieldset>
      </form>

      <div aria-live="polite">
        {!query ? (
          <p className="text-sm text-muted-600">
            Enter a term to search the demonstration collection.
          </p>
        ) : null}
        {query && results.isPending ? <LoadingState label="Searching" /> : null}
        {results.isError ? (
          <ErrorState title="Search failed" onRetry={() => void results.refetch()}>
            No results are shown when a search fails.
          </ErrorState>
        ) : null}
        {query && results.data?.length === 0 ? (
          <EmptyState title="Not found within the searched demonstration sources">
            This does not mean the law is silent. Only the selected demonstration collections were
            searched.
          </EmptyState>
        ) : null}
        {results.data && results.data.length > 0 ? (
          <ul className="flex flex-col gap-3">
            {results.data.map(({ source, version, passage }) => (
              <li key={passage.id} className="rounded-lg border border-line-200 bg-white p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <DataOriginBadge origin={source.dataOrigin} />
                  <span className="text-xs text-muted-600 capitalize">{source.kind}</span>
                  <span className="text-sm text-muted-600">
                    {source.court ?? source.citationAliases[0]}
                  </span>
                </div>
                <CitationLink
                  citation={{
                    id: `hit_${passage.id}`,
                    sourceId: source.id,
                    versionId: version.id,
                    passageIds: [passage.id],
                    label: `${source.title} — ${passage.label}`,
                    dataOrigin: source.dataOrigin,
                  }}
                />
                <p className="mt-2 line-clamp-3 font-serif text-ink-900">{passage.text}</p>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

const ACT_RESULTS_LIMIT = 60;

/**
 * The real Acts in the shared library (ADR-016): find an Act by title or year and open the
 * original document. Searching inside the text of Acts is a later step.
 */
function ActsSearch() {
  const tokens = useReaderTokens();
  const catalogue = useCatalogue(tokens);
  const inputId = useId();
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return null;
    return (catalogue.data?.acts ?? [])
      .filter((a) => {
        const haystack = `${a.title} ${displayTitle(a.title)} ${a.year ?? ''}`.toLowerCase();
        return terms.every((t) => haystack.includes(t));
      })
      .sort((a, b) => (b.year ?? 0) - (a.year ?? 0) || a.title.localeCompare(b.title));
  }, [catalogue.data, query]);

  if (!tokens) return <LibraryClosedNotice />;
  return (
    <section aria-labelledby="acts-search-heading" className="flex flex-col gap-4">
      <h2 id="acts-search-heading" className="font-serif text-xl font-semibold text-navy-900">
        Acts of Sierra Leone
      </h2>
      <ProvisionalNotice />
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="text-sm font-medium text-ink-900">
          Find an Act by title or year
        </label>
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. companies 2009"
          className="h-10 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600"
        />
        <p className="text-sm text-muted-600">
          Searches Act titles and years. Searching inside the text of Acts comes next.
        </p>
      </div>
      {catalogue.isPending ? <LoadingState label="Loading the Acts" /> : null}
      {catalogue.isError ? (
        <ErrorState title="The Acts could not be loaded" onRetry={() => void catalogue.refetch()}>
          {catalogue.error.message}
        </ErrorState>
      ) : null}
      <div aria-live="polite">
        {catalogue.data && !matches ? (
          <p className="text-sm text-muted-600">
            {catalogue.data.acts.length} Acts available. Enter a title or year to search them.
          </p>
        ) : null}
        {matches && matches.length === 0 ? (
          <EmptyState title="Not found within the Acts in the library">
            This does not mean no such Act exists; the library may not hold it yet.
          </EmptyState>
        ) : null}
        {matches && matches.length > 0 ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-900">
              {matches.length > ACT_RESULTS_LIMIT
                ? `Showing the first ${ACT_RESULTS_LIMIT} of ${matches.length} matching Acts`
                : `${matches.length} matching ${matches.length === 1 ? 'Act' : 'Acts'}`}
            </p>
            <ActList acts={matches.slice(0, ACT_RESULTS_LIMIT)} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

const TABS = [
  ['ask', 'Ask Buakei'],
  ['search', 'Search sources'],
  ['acts', 'Acts'],
] as const;
type Tab = (typeof TABS)[number][0];

/**
 * The student's learning workspace, for their own documents (ADR-045). The active workspace is
 * a professional one, so the learning workspace is found among the account's workspaces.
 */
function useLearningWorkspaceId(): string | undefined {
  const { session } = useAuth();
  const learning = session?.workspaces.filter((w) => w.kind === 'learning') ?? [];
  return (learning.find((w) => w.id === session?.activeWorkspaceId) ?? learning[0])?.id;
}

/**
 * The research workbench. Lawyers use it in Professional; students get the same screen in
 * Learning as the legal tutor (owner request), without saving memos.
 */
export function ResearchWorkbench({ learning = false }: { learning?: boolean }) {
  const [tab, setTab] = useState<Tab>('ask');
  // Verified accounts ask about the real Acts (ADR-017); DEMO personas keep the demo.
  const readerTokens = useReaderTokens();
  const memoWorkspace = useMemoWorkspace();
  const learningWorkspace = useLearningWorkspaceId();
  const documentsIn = learning ? learningWorkspace : memoWorkspace?.id;
  return (
    <Page
      fill={tab === 'ask' && Boolean(readerTokens)}
      title={learning ? 'Legal tutor' : 'Research workbench'}
      description={
        learning
          ? 'Ask about the law, or about your own notes and documents. Educational examples are labelled and never treated as real authority.'
          : 'Ask a question or search sources directly. Every citation opens the source inspector.'
      }
    >
      <div
        role="tablist"
        aria-label="Research tools"
        className="flex shrink-0 gap-1 border-b border-line-200"
      >
        {TABS.map(([value, label], index) => (
          <button
            key={value}
            role="tab"
            id={`tab-${value}`}
            aria-selected={tab === value}
            aria-controls={`panel-${value}`}
            tabIndex={tab === value ? 0 : -1}
            onClick={() => setTab(value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                const step = e.key === 'ArrowRight' ? 1 : TABS.length - 1;
                const next = TABS[(index + step) % TABS.length]![0];
                setTab(next);
                document.getElementById(`tab-${next}`)?.focus();
              }
            }}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
              tab === value
                ? 'border-navy-900 text-navy-900'
                : 'border-transparent text-muted-600 hover:text-navy-900',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id="panel-ask"
        aria-labelledby="tab-ask"
        hidden={tab !== 'ask'}
        className={tab === 'ask' && readerTokens ? 'flex min-h-0 flex-1 flex-col' : undefined}
      >
        {readerTokens ? (
          <ActsAssistant
            fill
            tokens={readerTokens}
            saveTo={learning ? undefined : memoWorkspace}
            documentsIn={documentsIn}
          />
        ) : learning ? (
          <AssistantPanel
            service={demoTutorAssistant}
            audience="learning"
            heading="Ask Buakei"
            placeholder="Ask about a principle in the demonstration materials…"
          />
        ) : (
          <AssistantPanel
            service={demoResearchAssistant}
            audience="professional"
            heading="Ask a legal research question"
            placeholder="Ask about the demonstration sources…"
            toolbar={<ResearchModeSelector />}
          />
        )}
      </div>
      <div role="tabpanel" id="panel-search" aria-labelledby="tab-search" hidden={tab !== 'search'}>
        <SourceSearch />
      </div>
      <div role="tabpanel" id="panel-acts" aria-labelledby="tab-acts" hidden={tab !== 'acts'}>
        {tab === 'acts' ? <ActsSearch /> : null}
      </div>
    </Page>
  );
}
